// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module StatsCard
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS summary-stat card used across analytics
 * page headers and dashboard compositions. The component combines an optional
 * icon, headline value, and trend indicator into a compact card surface.
 */

import { Badge, Card, CardContent } from "@geenius/ui/solidjs";
import { type Component, Show } from "solid-js";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

const SPARKLINE_WIDTH = 120;
const SPARKLINE_HEIGHT = 36;

function getSparklinePoints(values: number[]): string | null {
  const finiteValues = values.filter((value) => Number.isFinite(value));
  if (finiteValues.length < 2) return null;

  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  const range = max - min || 1;
  const lastIndex = finiteValues.length - 1;

  return finiteValues
    .map((value, index) => {
      const x = (index / lastIndex) * SPARKLINE_WIDTH;
      const y = SPARKLINE_HEIGHT - ((value - min) / range) * SPARKLINE_HEIGHT;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

/**
 * Props for rendering the SolidJS stats card.
 *
 * @property label Human-readable label for the metric being displayed.
 * @property value Formatted or raw metric value to display prominently.
 * @property icon Optional icon shown beside the metric content.
 * @property change Optional change string rendered below the main value.
 * @property trend Optional direction token used for the change indicator.
 * @property sparkline Optional numeric series rendered as a compact trend line.
 * @property sparklineLabel Accessible label for the sparkline image.
 * @property error Optional error value rendered instead of the metric body.
 * @property onRetry Optional recovery callback shown with the error state.
 * @property class Optional class name merged into the card wrapper.
 */
export interface StatsCardProps {
  label: string;
  value: string | number;
  icon?: string;
  change?: string;
  trend?: "up" | "down" | "neutral";
  sparkline?: number[];
  sparklineLabel?: string;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS summary-stat card.
 *
 * @param props Metric label, value, and optional trend presentation props.
 * @returns A card element showing the supplied summary metric.
 */
export const StatsCard: Component<StatsCardProps> = (props) => {
  const trendLabel = () =>
    props.trend === "up"
      ? "increased"
      : props.trend === "down"
        ? "decreased"
        : "changed";
  const sparklinePoints = () => getSparklinePoints(props.sparkline ?? []);

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardContent class="pt-6" data-analytics-stat>
          <ErrorState
            error={props.error}
            title={props.label}
            onRetry={props.onRetry}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardContent class="flex items-start gap-3 pt-6" data-analytics-stat>
        <Show when={props.icon}>
          <div class="text-xl" data-analytics-stat-icon aria-hidden="true">
            {props.icon}
          </div>
        </Show>
        <div
          class="flex min-w-0 flex-1 flex-col gap-1"
          data-analytics-stat-content
        >
          <span class="text-sm text-muted-foreground" data-analytics-stat-label>
            {props.label}
          </span>
          <span
            class="text-2xl font-semibold tracking-tight"
            data-analytics-stat-value
          >
            {props.value}
          </span>
          <Show when={props.change}>
            <Badge
              size="sm"
              variant={
                props.trend === "up"
                  ? "success"
                  : props.trend === "down"
                    ? "danger"
                    : "secondary"
              }
            >
              <span
                data-analytics-stat-change
                data-trend={props.trend || "neutral"}
                role="img"
                aria-label={`${trendLabel()} ${props.change}`}
              >
                <span aria-hidden="true">
                  {props.trend === "up"
                    ? "↑"
                    : props.trend === "down"
                      ? "↓"
                      : ""}
                </span>{" "}
                {props.change}
              </span>
            </Badge>
          </Show>
          <Show when={sparklinePoints()}>
            <svg
              class="mt-2 h-9 w-32 overflow-visible text-primary"
              data-analytics-stat-sparkline
              viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
              role={props.sparklineLabel ? "img" : undefined}
              aria-label={props.sparklineLabel}
              aria-hidden={props.sparklineLabel ? undefined : true}
            >
              <polyline
                class="fill-none stroke-current"
                points={sparklinePoints() ?? ""}
                stroke-width="2"
                vector-effect="non-scaling-stroke"
              />
            </svg>
          </Show>
        </div>
      </CardContent>
    </Card>
  );
};
